import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem,
  Chip, Switch, FormControlLabel, Alert, Snackbar, Tooltip, Grid, Divider, Badge
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Settings as SettingsIcon,
  TextFields as TextFieldsIcon, Numbers as NumbersIcon, Email as EmailIcon, Link as LinkIcon,
  AccessTime as TimeIcon, CalendarToday as DateIcon, List as ListIcon, ShortText as ShortTextIcon, Check as CheckIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import ClientDataService from '../../services/client.service';
import EmailTemplateService from '../../services/emailTemplate.service';
import { useTranslation } from '../../hooks/useTranslation';

const ClientFieldConfig = () => {
  const { hasPermission } = useAuth();
  const [fields, setFields] = useState([]);
  const [templates, setTemplates] = useState([]);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fieldTypes = [
    { value: 'text', label: t('clients.text'), icon: <TextFieldsIcon /> },
    { value: 'number', label: t('clients.number'), icon: <NumbersIcon /> },
    { value: 'date', label: t('clients.date'), icon: <DateIcon /> }
   /* 
    { value: 'textarea', label: 'Text Area', icon: <ShortTextIcon /> },
    { value: 'email', label: 'Email', icon: <EmailIcon /> },
    { value: 'url', label: 'URL', icon: <LinkIcon /> },
   
    { value: 'time', label: 'Time', icon: <TimeIcon /> },
    { value: 'select', label: 'Select', icon: <ListIcon /> }*/
  ];

  const [formData, setFormData] = useState({
    label: '',
    type: 'text',
    required: false,
    options: '',
    send_anniversary_email: false,
    email_template_id: ''
  });

  const fetchTemplates = async () => {
    try {
      const response = await EmailTemplateService.getAll();
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Error loading email templates:', error);
    }
  };

  useEffect(() => {
    fetchFields();
  }, []);

  const fetchFields = async () => {
    try {
      setLoading(true);
      const res = await ClientDataService.getFields();
      setFields(res.data);
    } catch (error) {
      console.error('Error loading fields:', error);
      showSnackbar(t('clients.errorLoadingFields'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenDialog = async (field = null) => {
    await fetchTemplates();

    if (field) {
      setEditingField(field);
      setFormData({
        label: field.label,
        type: field.type,
        required: field.required || false,
        options: field.options || '',
        send_anniversary_email: field.send_anniversary_email || false,
        email_template_id: field.email_template_id || ''
      });
    } else {
      setEditingField(null);
      setFormData({
        label: '',
        type: 'text',
        required: false,
        options: '',
        send_anniversary_email: false,
        email_template_id: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingField(null);
    setFormData({
      label: '',
      type: 'text',
      required: false,
      options: '',
      send_anniversary_email: false,
      email_template_id: ''
    });
  };

  const handleTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      type,
      send_anniversary_email: type === 'date' ? prev.send_anniversary_email : false,
      email_template_id: type === 'date' ? prev.email_template_id : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.type === 'date' && formData.send_anniversary_email && !formData.email_template_id) {
      showSnackbar(t('clients.emailTemplateRequired'), 'error');
      return;
    }

    try {
      const fieldData = {
        label: formData.label,
        type: formData.type,
        required: formData.required,
        send_anniversary_email: formData.type === 'date' ? formData.send_anniversary_email : false,
        email_template_id: formData.type === 'date' && formData.send_anniversary_email
          ? formData.email_template_id
          : null
      };

      if (formData.type === 'select' && formData.options) {
        fieldData.options = formData.options;
      }

      if (editingField) {
        await ClientDataService.updateField(editingField.id, fieldData);
        showSnackbar(t('clients.fieldUpdatedSuccess'));
      } else {
        await ClientDataService.createField(fieldData);
        showSnackbar(t('clients.fieldCreatedSuccess'));
      }

      handleCloseDialog();
      fetchFields();
    } catch (error) {
      console.error('Error saving field:', error);

      let errorMessage = t('clients.errorSavingField');
      if (error.response?.data?.errors) {
        errorMessage = Object.values(error.response.data.errors).flat().join(', ');
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      showSnackbar(errorMessage, 'error');
    }
  };

  const handleDelete = async (fieldId) => {
    if (window.confirm(t('clients.deleteFieldConfirmation'))) {
      try {
        await ClientDataService.deleteField(fieldId);
        showSnackbar(t('clients.deleteFieldSuccess'));
        fetchFields();
      } catch (error) {
        
        showSnackbar(t('clients.deleteFieldSuccessError'), 'error');
      }
    }
  };

  const getFieldIcon = (type) => {
    const fieldType = fieldTypes.find(ft => ft.value === type);
    return fieldType ? fieldType.icon : <TextFieldsIcon />;
  };

  const getFieldColor = (type) => {
    const colors = {
      text: 'primary',
      number: 'secondary',
      textarea: 'info',
      email: 'success',
      url: 'warning',
      date: 'info',
      time: 'error',
      select: 'default'
    };
    return colors[type] || 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading fields...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" component="h1">
            {t ('clients.fieldConfiguration')}
            </Typography>
            {hasPermission('clients.fields.add') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                {t ('clients.addfield')}
              </Button>
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" mb={3}>
          {t ('clients.msjconffield')}
          </Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell> {t ('clients.namefield')}</TableCell>
                  <TableCell>{t ('clients.Typefield')}</TableCell>
                  <TableCell>{t ('clients.emailfield')}</TableCell>
                  <TableCell>{t('clients.anniversaryTemplate')}</TableCell>
                  <TableCell align="left">{t ('clients.actionsfield')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((field) => (
                  <TableRow key={field.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getFieldIcon(field.type)}
                        <Typography variant="body2" fontWeight="medium">
                          {field.label}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={fieldTypes.find(ft => ft.value === field.type)?.label || field.type}
                        size="small"
                        color={getFieldColor(field.type)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={field.required ? 'Yes' : 'No'}
                        size="small"
                        color={field.required ? 'error' : 'default'}
                        variant={field.required ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      {field.type === 'date' && field.send_anniversary_email ? (
                        <Chip
                          label={field.email_template?.name || t('clients.anniversaryEmailEnabled')}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={1}>
                        {hasPermission('clients.fields.edit') && (
                          <Tooltip title="Edit field">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(field)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPermission('clients.fields.delete') && (
                          <Tooltip title="Delete field">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(field.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {fields.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box py={4}>
                        <SettingsIcon color="disabled" sx={{ fontSize: 48, mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                        {t ('clients.msjnoconffield')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                        {t ('clients.msjaddfield')}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Field Form Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <SettingsIcon color="primary" />
              {editingField ? t('clients.editField') : t('clients.createField')}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} pt={1}>
              <TextField
                label={t('clients.fieldLabel')}
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                required
                fullWidth
                placeholder="e.g., Phone Number, Company Size"
              />

              <FormControl fullWidth>
                <InputLabel>{t('clients.fieldType')}</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => handleTypeChange(e.target.value)}
                  label={t('clients.fieldType')}
                >
                  {fieldTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {type.icon}
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {formData.type === 'select' && (
                <TextField
                  label={t('clients.options')}
                  value={formData.options}
                  onChange={(e) => setFormData({ ...formData, options: e.target.value })}
                  fullWidth
                  placeholder={t('clients.optionsPlaceholder')}
                  helperText="Enter options separated by commas"
                />
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.required}
                    onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
                  />
                }
                label={t('clients.requiredField')}
              />

              {formData.type === 'date' && (
                <>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.send_anniversary_email}
                        onChange={(e) => setFormData({
                          ...formData,
                          send_anniversary_email: e.target.checked,
                          email_template_id: e.target.checked ? formData.email_template_id : ''
                        })}
                      />
                    }
                    label={t('clients.sendAnniversaryEmail')}
                  />

                  {formData.send_anniversary_email && (
                    <FormControl fullWidth required>
                      <InputLabel>{t('clients.emailTemplate')}</InputLabel>
                      <Select
                        value={formData.email_template_id}
                        onChange={(e) => setFormData({ ...formData, email_template_id: e.target.value })}
                        label={t('clients.emailTemplate')}
                      >
                        {templates.length === 0 ? (
                          <MenuItem disabled value="">
                            {t('clients.noEmailTemplatesAvailable')}
                          </MenuItem>
                        ) : (
                          templates.map((template) => (
                            <MenuItem key={template.id} value={template.id}>
                              {template.name}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  )}
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>{t('clients.cancel')}</Button>
            <Button type="submit" variant="contained">
              {editingField ? t('clients.updateField') : t('clients.createField')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientFieldConfig;
